'use client';

import { useEffect, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { EXTERNAL_APPLY_PATH } from '@/lib/recruitment/international';
import { TeliCareersHeader } from '@/components/recruitment/intl/IntlApplicationWizard';
import {
  DEFAULT_INTL_VACANCY,
  type IntlVacancy,
  parseIntlVacancy,
} from '@/lib/recruitment/intl-vacancies';
import {
  DEFAULT_JOB_APPLICATION_DEFINITIONS,
  type VacancyPublicInfo,
} from '@/lib/recruitment/job-application-definitions';

type Status = 'loading' | 'ready' | 'invalid';

export default function IntlJoinPage() {
  const { token } = useParams<{ token: string }>();
  const router = useRouter();
  const [status, setStatus] = useState<Status>('loading');
  // Each job application has its own shared link; the link says which job this is.
  // Texts as worded in Recruitment -> Job Applications (the API sends them with the vacancy).
  const [info, setInfo] = useState<VacancyPublicInfo>(DEFAULT_JOB_APPLICATION_DEFINITIONS[DEFAULT_INTL_VACANCY].info);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    fetch(`/api/public/intl-recruitment/intake/${token}`)
      .then(async (res) => {
        const d = await res.json().catch(() => null);
        const v: IntlVacancy = parseIntlVacancy(d?.vacancy) ?? DEFAULT_INTL_VACANCY;
        setInfo(
          d?.info && typeof d.info === 'object'
            ? (d.info as VacancyPublicInfo)
            : DEFAULT_JOB_APPLICATION_DEFINITIONS[v].info,
        );
        setStatus(res.ok && d?.ok ? 'ready' : 'invalid');
      })
      .catch(() => setStatus('invalid'));
  }, [token]);

  const start = async () => {
    setError('');
    setStarting(true);
    try {
      const res = await fetch(`/api/public/intl-recruitment/intake/${token}/start`, {
        method: 'POST',
      });
      const d = await res.json().catch(() => null);
      if (!res.ok || !d?.token) {
        setError(
          res.status === 429
            ? 'Too many requests - please wait a moment and try again.'
            : 'Could not start your application. Please try again.'
        );
        setStarting(false);
        return;
      }
      router.replace(EXTERNAL_APPLY_PATH(d.token));
    } catch {
      setError('Could not start your application. Check your connection and try again.');
      setStarting(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm">Loading…</p>
        </div>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-5xl mb-4">🔍</p>
          <h1 className="text-xl font-semibold text-white mb-2">This link is not available.</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <TeliCareersHeader subtitle={`${info.jobTitle} · ${info.tagline}`} />

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-400 mb-2">
            We&apos;re hiring in the Philippines
          </p>
          <h2 className="text-xl font-bold text-white mb-1">{info.jobTitle}</h2>
          <p className="text-sm text-gray-400 mb-5">{info.tagline}</p>

          <p className="text-sm text-gray-300 mb-5">{info.intro}</p>

          <p className="text-sm font-semibold text-gray-200 mb-2">What to have ready:</p>
          <ul className="space-y-1.5 mb-6">
            {info.haveReady.map((item) => (
              <li key={item} className="text-sm text-gray-400 flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 mt-1.5" />
                {item}
              </li>
            ))}
          </ul>

          {error && (
            <div className="mb-4 bg-red-900/30 border border-red-700 text-red-300 rounded-xl px-4 py-3 text-sm">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={() => void start()}
            disabled={starting}
            className="btn btn-primary w-full py-3.5 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60 h-auto"
          >
            {starting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Starting…
              </>
            ) : (
              'Start application'
            )}
          </button>

          <p className="mt-3 text-center text-xs text-gray-500">
            The application page that opens next is your personal resume link - keep that tab
            open or bookmark it so you can continue later.
          </p>
        </div>
      </div>
    </div>
  );
}
